import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, PlayCircle, CheckCircle2, ShieldCheck, ExternalLink, Download, Mail } from "lucide-react";
import api, { API } from "@/lib/api";
import { CONTACT } from "@/lib/constants";
import NotesGallery from "@/components/NotesGallery";

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <p className="font-serif text-2xl font-bold text-[#0B2545]">Course not found</p>
        <Link to="/courses" className="prose-link mt-3 inline-block">← Back to Courses</Link>
      </div>
    );
  }
  if (!course) {
    return <div className="flex min-h-[50vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-4 border-[#1F3A68] border-t-transparent" /></div>;
  }

  const handleEnroll = async () => {
    try { await api.post("/enrollments", { course_slug: course.slug }); } catch { /* best-effort lead capture */ }
    window.open(course.enroll_url, "_blank", "noopener");
  };

  return (
    <div>
      <div className="bg-[#0B2545]">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <Link to="/courses" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="back-to-courses">
            <ArrowLeft size={15} /> Back to Courses
          </Link>
          <h1 className="mt-3 font-serif text-2xl font-bold text-white md:text-4xl">{course.title}</h1>
          <p className="mt-2 max-w-2xl text-slate-200">{course.subtitle}</p>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-slate-300">
            <span className="flex items-center gap-1"><Clock size={15} /> {course.duration}</span>
            <span className="flex items-center gap-1"><PlayCircle size={15} /> {course.lessons} lessons</span>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div className="mb-6 rounded-md border-2 border-[#FF9933] bg-[#FEF3C7] p-5 md:p-6" data-testid="community-notice">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#FF9933] md:h-12 md:w-12">
                <Mail size={22} className="text-slate-900" />
              </div>
              <div>
                <h3 className="font-serif text-xl font-bold text-[#0B2545] md:text-2xl">After you enrol, join the community!</h3>
                <p className="mt-2 text-base leading-relaxed text-[#7c3a06] md:text-lg">
                  Once your payment is complete, <strong>email your payment receipt to{" "}
                  <a href={`mailto:${CONTACT.email}`} className="font-bold text-[#0B2545] underline">{CONTACT.email}</a></strong>{" "}
                  to get added to the exclusive student community and receive your course access.
                </p>
              </div>
            </div>
          </div>
          <img src={course.image} alt={course.title} className="w-full rounded-sm border border-[#D1D5DB] object-cover" />

          {course.sample_notes_images?.length > 0 && (
            <NotesGallery slug={course.slug} count={course.sample_notes_images.length} />
          )}
          <div className="gov-card mt-6 overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-base font-bold">About this Course</div>
            <div className="p-5">
              <p className="text-sm leading-relaxed text-gray-700">{course.description}</p>
            </div>
          </div>
          <div className="gov-card mt-6 overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-base font-bold">What you'll get</div>
            <ul className="space-y-3 p-5">
              {course.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-gray-700">
                  <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#16A34A]" /> {f}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Enroll box */}
        <div className="lg:col-span-1">
          <div className="gov-card sticky top-20 overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-base font-bold">Enrol Now</div>
            <div className="p-5">
              <div className="flex items-end gap-2">
                <span className="font-serif text-3xl font-bold text-[#0B2545]">₹{course.price}</span>
                {course.original_price && <span className="mb-1 text-base text-gray-400 line-through">₹{course.original_price}</span>}
              </div>
              {course.original_price && (
                <p className="mt-1 text-sm font-semibold text-[#16A34A]">
                  Save {Math.round((1 - course.price / course.original_price) * 100)}% — limited period offer
                </p>
              )}
              <button
                onClick={handleEnroll}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-sm bg-[#FF9933] px-4 py-3 text-sm font-bold text-slate-900 transition-colors hover:bg-[#E68A2E]"
                data-testid="enroll-btn"
              >
                Enrol via Razorpay <ExternalLink size={15} />
              </button>
              <div className="mt-3 rounded-sm border border-[#FF9933] bg-[#FEF3C7] p-3 text-sm leading-relaxed text-[#7c3a06]" data-testid="enroll-receipt-note">
                <strong className="text-[#0B2545]">Important:</strong> After paying, email your receipt to{" "}
                <a href={`mailto:${CONTACT.email}`} className="font-semibold text-[#0B2545] underline">{CONTACT.email}</a>{" "}
                to join the community &amp; get access.
              </div>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-gray-500">
                <ShieldCheck size={14} className="text-[#16A34A]" /> Secure payment via Razorpay
              </p>
              {course.brochure_path && (
                <a href={`${API}/courses/${course.slug}/brochure`} target="_blank" rel="noopener noreferrer" className="mt-3 flex w-full items-center justify-center gap-2 rounded-sm border border-[#1F3A68] px-4 py-2.5 text-sm font-semibold text-[#1F3A68] transition-colors hover:bg-[#1F3A68] hover:text-white" data-testid="brochure-btn">
                  <Download size={15} /> Download Brochure (PDF)
                </a>
              )}
              <div className="mt-4 border-t border-[#E5E7EB] pt-4 text-xs text-gray-500">
                Lifetime access · Recorded lessons · Downloadable notes
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
